
import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface FeedbackButtonsProps {
  messageId: number;
  onFeedbackSubmit: (messageId: number, helpful: boolean) => Promise<void>;
  messageRole: 'user' | 'assistant';
}

const FeedbackButtons: React.FC<FeedbackButtonsProps> = ({ 
  messageId, 
  onFeedbackSubmit,
  messageRole
}) => {
  const [feedbackGiven, setFeedbackGiven] = useState<boolean | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Only assistant messages can receive feedback
  if (messageRole !== 'assistant') return null;

  const handleFeedback = async (helpful: boolean) => {
    if (isSubmitting) return;
    
    try {
      setIsSubmitting(true);
      await onFeedbackSubmit(messageId, helpful);
      setFeedbackGiven(helpful);
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (feedbackGiven !== null) {
    return (
      <div className="text-xs text-muted-foreground mt-1 italic">
        Thank you for your feedback!
      </div>
    );
  }

  return (
    <div className="flex gap-2 mt-1">
      <Button 
        variant="ghost" 
        size="icon" 
        className="h-6 w-6" 
        onClick={() => handleFeedback(true)}
        disabled={isSubmitting}
        aria-label="Mark as helpful"
      >
        <ThumbsUp className="h-3.5 w-3.5" />
      </Button>
      <Button 
        variant="ghost" 
        size="icon" 
        className="h-6 w-6"
        onClick={() => handleFeedback(false)}
        disabled={isSubmitting}
        aria-label="Mark as not helpful"
      >
        <ThumbsDown className="h-3.5 w-3.5" />
      </Button>
      <span className="text-xs text-muted-foreground">Was this helpful?</span>
    </div>
  );
};

export default FeedbackButtons;
